// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { consentNeeded } from '@selfservice/utils/helpers';
import type { CheckDpaConsentResponseActionType, ResidenceLocation } from '@api/types';
import { ConsentGrantType } from '@api/constants';
import ConsentDocumentScreen from './ConsentDocumentScreen';
import ConsentStateSelection from './ConsentStateSelection';
import { useConsentData } from './useConsentData';
import { CUSTOMER_NOTICE, STATE_SELECTION, CUSTOMER_CONSENT, IMPRIVATA_CONSENT } from './constants';
import './ConsentScreens.less';

const BIOMETRIC_NOTICE = 'biometric-notice' as const;

export type ScreenState =
  | typeof CUSTOMER_NOTICE
  | typeof STATE_SELECTION
  | typeof CUSTOMER_CONSENT
  | typeof IMPRIVATA_CONSENT;

export interface ConsentScreensProps {
  onConsent: (userConsentResponse: ConsentGrantType, stateOfResidence: string, documentNameList: string[]) => void;
  onCancel: () => void;
  onContinue: () => void;
  residenceLocations?: ResidenceLocation[];
}

export const ConsentScreens = ({ onContinue, onConsent, onCancel,  residenceLocations = [] }: ConsentScreensProps) => {
  const { t } = useTranslation();
  const [pickedState, setPickedState] = useState<string | undefined>();
  const [screenState, setScreenState] = useState<ScreenState>(CUSTOMER_NOTICE);

  const {
    consentQuery,
    customerNoticeDoc,
    shouldShowCustomerConsent,
    shouldShowImprivataConsent,
    imprivataConsentDoc,
    customerConsentDoc,
  } = useConsentData(pickedState);

  useEffect(() => {
    const skipImprivataConsent = !shouldShowImprivataConsent || !imprivataConsentDoc;
    const skipCustomerConsent = !shouldShowCustomerConsent || !customerConsentDoc;

    if (screenState === IMPRIVATA_CONSENT && skipImprivataConsent) {
      if (skipCustomerConsent) {
        // No customer consent needed, complete the flow
        onConsent(
          ConsentGrantType.EMPTY, // If no consent is required, set to EMPTY
          pickedState || '',
          consentQuery.data?.documentMetadata
            .filter((d) => d.documentPurpose !== BIOMETRIC_NOTICE)
            .map((doc) => doc.documentName) || [],
        );
      } else {
        setScreenState(CUSTOMER_CONSENT);
      }
    } else if (screenState === CUSTOMER_CONSENT && skipCustomerConsent) {
      // No customer consent needed, complete the flow
      onConsent(
        ConsentGrantType.GRANTED,
        pickedState || '',
        consentQuery.data?.documentMetadata
          .filter((d) => d.documentPurpose !== BIOMETRIC_NOTICE)
          .map((doc) => doc.documentName) || [],
      );
    }
  }, [
    screenState,
    shouldShowImprivataConsent,
    imprivataConsentDoc,
    shouldShowCustomerConsent,
    customerConsentDoc,
    onConsent,
    pickedState,
    consentQuery.data,
  ]);

  if (consentQuery.isLoading) {
    return (
      <div className="consent-screens">
        <div className="consent-screens__content">
          <p className='consent-screens__loading'>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // SCREEN-1: Customer Notice
  if (screenState === CUSTOMER_NOTICE) {
    const handleCustomerNoticeContinue = () => {
      // Check if consent is actually needed using the consentNeeded helper
      if (consentQuery.data) {
        const biometricsConsentInfo = {
          actionType: consentQuery.data.actionType as CheckDpaConsentResponseActionType,
          grantType: consentQuery.data.grantType as ConsentGrantType,
        };

        if (consentNeeded(biometricsConsentInfo)) {
          setScreenState(STATE_SELECTION);
        } else {
          onContinue();
        }
      } else {
        // If no data, assume consent is needed and go to state selection
        setScreenState(STATE_SELECTION);
      }
    };

    return (
      <ConsentDocumentScreen
        description={t('self-service.consent-screens.notice-description')}
        documentUrl={customerNoticeDoc?.readUrl}
        onContinue={handleCustomerNoticeContinue}
        onCancel={onCancel}
        continueLabel={t('actions.continue')}
        cancelLabel={t('common.do-later')}
      />
    );
  }

  // SCREEN-2: State Selection
  if (screenState === STATE_SELECTION) {
    return (
      <ConsentStateSelection
        residenceLocations={residenceLocations}
        onSelect={(state) => {
          setPickedState(state);
          setScreenState(IMPRIVATA_CONSENT);
        }}
        onCancel={onCancel}
      />
    );
  }

  // SCREEN-3: Imprivata Consent
  if (screenState === IMPRIVATA_CONSENT) {
    // Check if imprivata consent is needed
    if (shouldShowImprivataConsent && imprivataConsentDoc) {
      const handleImprivataConsentContinue = () => {
        // Check if customer consent is needed after Imprivata consent
        if (shouldShowCustomerConsent && customerConsentDoc) {
          setScreenState(CUSTOMER_CONSENT);
        } else {
          onConsent(
            ConsentGrantType.GRANTED,
            pickedState || '',
            consentQuery.data?.documentMetadata
              .filter((d) => d.documentPurpose !== BIOMETRIC_NOTICE)
              .map((doc) => doc.documentName) || [],
          );
        }
      };

      return (
        <ConsentDocumentScreen
          title={t('self-service.consent-screens.title-consent', { state: pickedState })}
          documentUrl={imprivataConsentDoc?.readUrl}
          onContinue={handleImprivataConsentContinue}
          onCancel={() => {
            onConsent(
              ConsentGrantType.NOT_GRANTED,
              pickedState || '',
              consentQuery.data?.documentMetadata
                .filter((d) => d.documentPurpose !== BIOMETRIC_NOTICE)
                .map((doc) => doc.documentName) || [],
            );
            onCancel();
          }}
        />
      );
    }

    return null;
  }

  // SCREEN-4: Customer Consent
  if (screenState === CUSTOMER_CONSENT) {
    // Check if customer consent is needed
    if (shouldShowCustomerConsent && customerConsentDoc) {
      const handleCustomerConsentContinue = () => {
        onConsent(
          ConsentGrantType.GRANTED,
          pickedState || '',
          consentQuery.data?.documentMetadata
            .filter((d) => d.documentPurpose !== BIOMETRIC_NOTICE)
            .map((doc) => doc.documentName) || [],
        );
      };

      return (
        <ConsentDocumentScreen
          title={t('self-service.consent-screens.title-consent', { state: pickedState })}
          documentUrl={customerConsentDoc.readUrl}
          onContinue={handleCustomerConsentContinue}
          onCancel={() => {
            onConsent(
              ConsentGrantType.NOT_GRANTED,
              pickedState || '',
              consentQuery.data?.documentMetadata
                .filter((d) => d.documentPurpose !== BIOMETRIC_NOTICE)
                .map((doc) => doc.documentName) || [],
            );
            onCancel();
          }}
        />
      );
    }
    // Return null while useEffect handles the completion
    return null;
  }

  return null;
};
